'use client';

import { $generateHtmlFromNodes } from '@lexical/html';
import { type InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import type { EditorState, SerializedEditorState } from 'lexical';

import { editorTheme } from '../../editor/themes/editor-theme';
import { TooltipProvider } from '../../ui/tooltip';

import { nodes } from './nodes';
import { Plugins } from './plugins';

const editorConfig: InitialConfigType = {
  namespace: 'Editor',
  theme: editorTheme,
  nodes,
  onError: (error: Error) => {
    console.error(error);
  },
};

interface Variable {
  name: string;
  label: string;
}

export function Editor({
  editorState,
  editorSerializedState,
  onChange,
  onSerializedChange,
  onHtmlChange,
  onImageUpload,
  variables,
  height,
  isInDialog,
  imageUploadEnabled = true,
}: {
  editorState?: EditorState;
  editorSerializedState?: SerializedEditorState;
  onChange?: (editorState: EditorState) => void;
  onSerializedChange?: (editorSerializedState: SerializedEditorState) => void;
  onHtmlChange?: (html: string) => void;
  onImageUpload?: (file: File) => Promise<string>;
  variables?: Variable[];
  height?: string;
  isInDialog?: boolean;
  imageUploadEnabled?: boolean;
}) {
  return (
    <div className="bg-background rounded-lg border shadow max-w-full h-[300px] overflow-auto">
      <LexicalComposer
        initialConfig={{
          ...editorConfig,
          ...(editorState ? { editorState } : {}),
          ...(editorSerializedState ? { editorState: JSON.stringify(editorSerializedState) } : {}),
        }}
      >
        <TooltipProvider>
          <Plugins
            onImageUpload={onImageUpload}
            variables={variables}
            height={height}
            isInDialog={isInDialog}
            imageUploadEnabled={imageUploadEnabled}
          />

          <OnChangePlugin
            ignoreSelectionChange={true}
            onChange={(editorState, editor) => {
              onChange?.(editorState);
              onSerializedChange?.(editorState.toJSON());

              if (onHtmlChange) {
                editorState.read(() => {
                  const html = $generateHtmlFromNodes(editor);
                  onHtmlChange(html);
                });
              }
            }}
          />
        </TooltipProvider>
      </LexicalComposer>
    </div>
  );
}
